import { useNavigate } from 'react-router-dom';
import { Badge, Text } from '@repletoryreach/sdk-ui';
import { DataTable } from '@repletoryreach/sdk-ui/data-display';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import type { templates } from '@repletoryreach/sdk-api';
import { formatDateTime, formatRelative } from '../../../lib/format';
import { TwoLineCell } from '../../../components/TwoLineCell';
import { TemplateStatusBadge } from './TemplateStatusBadge';
import { RowMenu } from '../../../components/RowMenu';
import type { Indexed } from '../../../lib/types';
import { useT } from '../../../i18n/I18nProvider';

type Template = NonNullable<Awaited<ReturnType<typeof templates.list>>['data']>[number];

const MUTED = 'var(--rr-color-text-muted)';

export function TemplatesTable({ rows, loading }: { rows: Template[]; loading: boolean }) {
  const navigate = useNavigate();
  const t = useT();
  const dash = <Text role="caption" c={MUTED}>{t('common.notAvailable')}</Text>;

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      notifySuccess(value, t('common.copied'));
    } catch {
      // A denied clipboard is the browser's decision to report, not ours.
    }
  }

  return (
    <DataTable<Indexed<Template>>
      records={rows as Indexed<Template>[]}
      loading={loading}
      idAccessor="id"
      emptyState={t('templates.table.empty')}
      onRowClick={(row) => navigate(`/templates/${row.id}`)}
      columns={[
        {
          accessor: 'code',
          title: t('templates.columns.template'),
          sortable: true,
          // The code is what a send quotes; Meta's own display name rides
          // under it, because the two drift and the divergence matters.
          render: (r) => <TwoLineCell primary={r.code} secondary={r.meta_template_name ?? undefined} title={r.code} />
        },
        { accessor: 'category', title: t('templates.columns.category') },
        { accessor: 'language', title: t('templates.columns.language') },
        {
          accessor: 'status',
          title: t('common.status'),
          // Meta's reasons run long, so the reason clamps under the badge and
          // the whole text is on hover rather than being silently cut.
          render: (r) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
              <TemplateStatusBadge status={r.status} devFallback={r.dev_fallback} />
              {r.rejection_reason && (
                <Tooltip label={r.rejection_reason} position="top" openDelay={200}>
                  <Text role="caption" c="var(--rr-color-error)" lineClamp={2} style={{ maxWidth: 'min(32ch, 100%)' }}>
                    {r.rejection_reason}
                  </Text>
                </Tooltip>
              )}
            </div>
          )
        },
        {
          accessor: 'meta_status',
          title: t('templates.columns.metaStatus'),
          // Its own column rather than a chip beside ours: an operator reading
          // this page is comparing the two words, and a column of them can be
          // scanned where an inline badge cannot.
          render: (r) => r.meta_status
            ? (
              <Tooltip label={t('templates.metaStatusTooltip')} position="top" openDelay={200}>
                <Badge size="xs" variant="light" color={r.meta_status.toLowerCase() === r.status.toLowerCase() ? 'gray' : 'orange'}>
                  {r.meta_status}
                </Badge>
              </Tooltip>
            )
            : <Text role="caption" c={MUTED}>{t('templates.metaStatusNone')}</Text>
        },
        {
          accessor: 'updated_at',
          title: t('templates.columns.updatedAt'),
          sortable: true,
          render: (r) => <TwoLineCell primary={formatDateTime(r.updated_at)} secondary={formatRelative(r.updated_at)} />
        },
        {
          accessor: 'imported_at',
          title: t('templates.columns.importedAt'),
          sortable: true,
          render: (r) => r.imported_at ? <TwoLineCell primary={formatDateTime(r.imported_at)} secondary={formatRelative(r.imported_at)} /> : dash
        },
        {
          accessor: 'actions',
          title: t('common.actions'),
          width: 64,
          textAlign: 'right',
          render: (r) => (
            <RowMenu
              actions={[
                { id: 'view', label: t('templates.actions.view'), onSelect: () => navigate(`/templates/${r.id}`) },
                { id: 'code', label: t('templates.actions.copyCode'), onSelect: () => void copy(r.code) }
              ]}
            />
          )
        }
      ]}
    />
  );
}
